<!--
  VersionHistoryPanel.vue
  (Phase 2.1 / Phase 3.2 -- card https://trello.com/c/hYlwyKYT).

  Version list for an Artwork with a Restore action. Per P3-Q7,
  Restore MINTS A NEW VERSION (it does not edit in place): the parent
  forwards the click to ``store.restoreVersion(artworkId, source)``
  which POSTs to /api/artworks/<id>/versions with the source version's
  ``file_url``. The new version becomes the artwork's
  ``current_version`` server-side.

  SHARED COMPONENT contract. Used by:
    - Phase 2 Approval Workspace -- detail panel; assumes "newest =
      current" (backcompat default).
    - Phase 3 Decoration Detail page -- passes ``current-version-id``
      explicitly. After a restore the highest-numbered row is
      NOT the current row (its file came from an older version), so
      Phase 3 must declare which row is current.

  Props
  -----

  - ``versions`` : array of ArtworkVersion-shaped objects
      ``{id, version_number, file_url, digitized_at, digitizer_id,
        created_at}``
  - ``artworkId`` : number | string
  - ``canRestore`` : boolean -- gates the Restore CTA.
  - ``pending`` : boolean    -- disables CTAs while a mutation runs.
  - ``currentVersionId`` : number | null
      Phase 3.2. When provided, the row with this id receives the
      "Current" badge and its Restore CTA is hidden. When null, the
      panel falls back to "newest version_number = current" so the
      Phase 2 Approval Workspace contract remains intact.
  - ``usageByVersion`` : { [version_id]: count } | null
      Phase 3.2. Optional per-version product-binding counts. When
      supplied, each row renders a "used by N products" subline.

  Events
  ------
  - ``restore(version)`` -- payload is the source version object;
                            parent calls the store.

  A confirmation dialog ("Restore vN as a new version? This creates
  v{N+1} with vN's file.") prevents accidental clicks; it is a plain
  modal owned by this component so the parent doesn't have to track
  restore state.
-->
<template>
    <div class="aw-versions" data-testid="version-history">
        <div class="aw-versions__header">
            <span
                class="aw-versions__title"
                data-testid="version-history-title"
                >Version history</span
            >
            <span class="aw-versions__count">{{ versions.length }}</span>
        </div>
        <div
            v-if="!versions.length"
            class="aw-versions__empty"
            data-testid="version-history-empty"
        >
            No version history yet.
        </div>
        <ul v-else class="aw-versions__list">
            <li
                v-for="v in sortedVersions"
                :key="v.id"
                class="aw-versions__row"
                :data-testid="`version-history-row-${v.id}`"
            >
                <span class="aw-versions__tag">v{{ v.version_number }}</span>
                <span class="aw-versions__meta">
                    <span class="aw-versions__filename">{{
                        v.file_url || 'No file URL on record'
                    }}</span>
                    <span class="aw-versions__sub">
                        {{ formatWhen(v.digitized_at || v.created_at) }}
                        <template v-if="isCurrent(v)">
                            <span
                                class="aw-versions__current-badge"
                                data-testid="version-history-current-badge"
                                >Current</span
                            >
                        </template>
                    </span>
                    <span
                        v-if="usageCountFor(v) !== null"
                        class="aw-versions__usage"
                        :data-testid="`version-history-usage-${v.id}`"
                        >used by {{ usageCountFor(v) }}
                        product{{
                            usageCountFor(v) === 1 ? '' : 's'
                        }}</span
                    >
                </span>
                <button
                    v-if="canRestore && !isCurrent(v)"
                    type="button"
                    class="aw-versions__restore"
                    :disabled="pending"
                    :data-testid="`version-history-restore-${v.id}`"
                    @click="openConfirm(v)"
                >
                    Restore
                </button>
            </li>
        </ul>

        <div
            v-if="confirmTarget"
            class="aw-versions__modal-backdrop"
            data-testid="version-history-confirm"
            @click.self="cancelConfirm"
        >
            <div class="aw-versions__modal" role="dialog" aria-modal="true">
                <h4 class="aw-versions__modal-title">
                    Restore v{{ confirmTarget.version_number }} as a new
                    version?
                </h4>
                <p class="aw-versions__modal-body">
                    This creates v{{ nextVersionNumber }} with v{{
                        confirmTarget.version_number
                    }}'s file.
                </p>
                <div class="aw-versions__modal-actions">
                    <button
                        type="button"
                        class="aw-versions__btn"
                        data-testid="version-history-confirm-cancel"
                        @click="cancelConfirm"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="aw-versions__btn aw-versions__btn--primary"
                        :disabled="pending"
                        data-testid="version-history-confirm-restore"
                        @click="confirmRestore"
                    >
                        Restore
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'VersionHistoryPanel',
    props: {
        versions: {
            type: Array,
            default: () => [],
        },
        artworkId: {
            type: [Number, String, null],
            default: null,
        },
        canRestore: {
            type: Boolean,
            default: false,
        },
        pending: {
            type: Boolean,
            default: false,
        },
        /**
         * Phase 3.2. Parent declares the artwork's current_version_id
         * so the panel can mark that row "Current" -- even when it is
         * not the highest version_number (which happens after a
         * restore, since restore mints a new version whose number is
         * highest but whose file is from an older row).
         *
         * When null, the panel falls back to "newest version_number =
         * current," preserving the Phase 2 Approval Workspace
         * contract.
         */
        currentVersionId: {
            type: [Number, String, null],
            default: null,
        },
        /**
         * Phase 3.2. Optional per-version product-binding counts:
         *
         *   { [version_id]: count }
         *
         * When supplied, each version row renders a "used by N
         * products" subline. When ``null`` the subline is omitted.
         */
        usageByVersion: {
            type: Object,
            default: null,
        },
    },
    emits: ['restore'],
    data() {
        return {
            confirmTarget: null,
        }
    },
    computed: {
        sortedVersions() {
            // Newest first; the design's "current" row is the topmost
            // when no explicit currentVersionId is supplied.
            return [...(this.versions || [])].sort(
                (a, b) =>
                    (b.version_number || 0) - (a.version_number || 0),
            )
        },
        /**
         * Highest version_number in the list. Used to compute "next
         * version number" for the restore-confirmation copy, since the
         * server's mint sequence is ``max(version_number) + 1`` per
         * the F1.d ArtworkService contract.
         */
        highestVersionNumber() {
            let max = 0
            for (const v of this.versions || []) {
                if ((v.version_number || 0) > max) {
                    max = v.version_number || 0
                }
            }
            return max
        },
        /**
         * Per-spec, "This creates v{N+1} with v{N}'s file" -- where
         * N+1 is the next mint number, not the source's number + 1.
         */
        nextVersionNumber() {
            return this.highestVersionNumber + 1
        },
        /**
         * Which row id should receive the "Current" badge:
         *   - prefer parent-declared currentVersionId
         *   - fall back to newest version_number (Phase 2 backcompat)
         */
        effectiveCurrentVersionId() {
            if (
                this.currentVersionId !== null &&
                this.currentVersionId !== undefined
            ) {
                return this.currentVersionId
            }
            const newest = this.sortedVersions[0]
            return newest ? newest.id : null
        },
    },
    methods: {
        isCurrent(v) {
            return v && v.id === this.effectiveCurrentVersionId
        },
        usageCountFor(v) {
            if (!this.usageByVersion || !v) return null
            const c = this.usageByVersion[v.id]
            if (c === undefined || c === null) return null
            return c
        },
        formatWhen(iso) {
            if (!iso) return ''
            const d = new Date(iso)
            if (Number.isNaN(d.getTime())) return iso
            return d.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            })
        },
        openConfirm(v) {
            this.confirmTarget = v
        },
        cancelConfirm() {
            this.confirmTarget = null
        },
        confirmRestore() {
            const target = this.confirmTarget
            if (!target) return
            this.confirmTarget = null
            this.$emit('restore', target)
        },
    },
}
</script>

<style scoped>
.aw-versions {
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-sm, 6px);
    background: var(--tf-bg, #ffffff);
    margin-bottom: 16px;
}
.aw-versions__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: var(--tf-surface-alt, #f7fafc);
    border-bottom: 1px solid var(--tf-border, #e2e8f0);
    font-size: 12px;
}
.aw-versions__title {
    font-weight: 600;
    color: var(--tf-fg-1, #1a202c);
}
.aw-versions__count {
    color: var(--tf-fg-4, #5a6a82);
}
.aw-versions__empty {
    padding: 20px 14px;
    color: var(--tf-fg-4, #5a6a82);
    font-size: 13px;
    text-align: center;
}
.aw-versions__list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.aw-versions__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--tf-border, #f1f5f9);
}
.aw-versions__row:last-child {
    border-bottom: none;
}
.aw-versions__tag {
    flex-shrink: 0;
    padding: 2px 8px;
    background: var(--tf-surface-alt, #edf2f7);
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-fg-3, #4a5568);
}
.aw-versions__meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
}
.aw-versions__filename {
    font-size: 13px;
    color: var(--tf-fg-1, #1a202c);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.aw-versions__sub {
    font-size: 11px;
    color: var(--tf-fg-4, #5a6a82);
    display: flex;
    align-items: center;
    gap: 6px;
}
.aw-versions__current-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 8px;
    background: var(--tf-primary-subtle, #e3f2fd);
    color: var(--tf-primary, #1976d2);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: none;
}
.aw-versions__usage {
    font-size: 11px;
    color: var(--tf-fg-4, #5a6a82);
}
.aw-versions__restore {
    padding: 4px 10px;
    border: 1px solid var(--tf-border-strong, #cbd5e0);
    background: var(--tf-bg, #ffffff);
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
}
.aw-versions__restore:hover:not(:disabled) {
    background: var(--tf-surface-alt, #edf2f7);
}
.aw-versions__restore:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.aw-versions__modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.aw-versions__modal {
    background: #ffffff;
    border-radius: 8px;
    width: min(420px, 92vw);
    padding: 20px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
}
.aw-versions__modal-title {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 600;
}
.aw-versions__modal-body {
    margin: 0 0 16px;
    font-size: 13px;
    color: var(--tf-fg-3, #4a5568);
    line-height: 1.5;
}
.aw-versions__modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
.aw-versions__btn {
    padding: 6px 14px;
    border: 1px solid var(--tf-border-strong, #cbd5e0);
    background: var(--tf-bg, #ffffff);
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
}
.aw-versions__btn--primary {
    background: var(--tf-primary, #1976d2);
    color: #ffffff;
    border-color: var(--tf-primary, #1976d2);
}
.aw-versions__btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
</style>
